/*eslint @typescript-eslint/no-explicit-any: ["error", { "ignoreRestArgs": true }]*/
import React, { useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import { HubConnectionBuilder } from '@microsoft/signalr'
import '@styles/index.scss';

import {
	MeetingPage,
	MeetingListPage,
	NotFoundPage,
	CallbackFromSSOPage,
	AuthenticationErrorPage,
} from '@pages';

import {
	ModalProvider,
} from '@components';

import {
	BrowserRouter as Router,
	Switch,
	Route,
} from "react-router-dom";

import {
	routes,
} from '@routes';

import {
	MeetingsStoreProvider,
	UserStoreProvider,
} from '@store';


import {
	useModal,
} from '@common';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AboutModal } from '@modals';
import { authService } from '@services';

moment.locale('ru');

window.onerror = function (message, file, line, col, error) {
	toast.error(`Ошибка: ${message}`, {autoClose: false });
	console.error(message, {file, line, col, error});
	return true;
 };

function App(): JSX.Element {

	const {
		isModalOpen: isAboutModalOpen,
		closeModal: closeAboutModal,
		openModal: openAboutModal,
	} = useModal();

	const handleKeyDown = useCallback((ev) => {	
		if (ev.key == 'F1') {
			if (!isAboutModalOpen) {
				openAboutModal();
			} else {
				closeAboutModal();
			}
			ev.preventDefault();
		} 
	}, [openAboutModal, closeAboutModal, isAboutModalOpen]);

	const tokenFunc = async () => {
		return await authService.getAccessToken();
	}

	const signalRConnection = new HubConnectionBuilder()
		.withUrl(`/api/hub/notification`, {
			accessTokenFactory: tokenFunc,					
		})
		// .configureLogging(LogLevel.Trace)
		.build();

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		
		async function configureSignalR() {			
			await signalRConnection.start();
		}

		configureSignalR();
		
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        }

	}, [handleKeyDown, signalRConnection]);

	let notificationPages = [];

	const registerNotification = (page: string, name: string, handler: (...args: any[]) => void) => {
		const notifPageKey = `${page}_${name}`;
		if (!notificationPages.includes(notifPageKey)) {
			signalRConnection.on(name, handler);
			notificationPages.push(notifPageKey);
		}
	};

	const unRegisterNotification = (page: string, name: string, handler: (...args: any[]) => void) => {
		const notifPageKey = `${page}_${name}`;
		notificationPages = notificationPages.filter(v => v != notifPageKey);
		signalRConnection.off(name, handler);
	};

	const aboutModalView = useMemo((): JSX.Element => {
		return isAboutModalOpen && (
			<AboutModal
				closeModal={closeAboutModal}
			/>
		);
	}, [closeAboutModal, isAboutModalOpen]);

	return (
		<ModalProvider>
		<UserStoreProvider>
		<MeetingsStoreProvider>
			<Router>
				<div className="App" onKeyDown={handleKeyDown}>
					<Switch>
						<Route path={routes.ssoCallback.path} exact={routes.ssoCallback.exact}>
							<CallbackFromSSOPage />
						</Route>
						<Route path={[routes.meetingListPage.path, routes.index.path]} exact={routes.meetingListPage.exact}>
							<MeetingListPage 
								registerNotificationHandler={registerNotification.bind(this, "MeetingListPage")} 
								unRegisterNotificationHandler={unRegisterNotification.bind(this, "MeetingListPage")}
							/>
						</Route>
						<Route path={routes.meetingPage.path}>
							<MeetingPage
								registerNotificationHandler={registerNotification.bind(this, "MeetingPage")}
								unRegisterNotificationHandler={unRegisterNotification.bind(this, "MeetingPage")}
							/>
						</Route>
						<Route path={routes.authenticationError.path} exact={routes.authenticationError.exact}>
							<AuthenticationErrorPage />
						</Route>
						<Route>
							<NotFoundPage />
						</Route>
					</Switch>
					{aboutModalView}
					<ToastContainer theme='light' />
				</div>
			</Router>
		</MeetingsStoreProvider>
		</UserStoreProvider>
		</ModalProvider>
	);
}

export default App;
